import { Injectable } from '@angular/core';
import { DayCountConvention } from '../models/loan.model';

@Injectable({
  providedIn: 'root',
})
export class DayCountService {
  /**
   * Gets the number of days in a year based on the specified day count convention.
   * This is used as the denominator in daily interest calculations.
   * @param convention The day count convention to use.
   * @returns The number of days in the year for calculation (e.g., 365 or 360).
   */
  getDaysInYear(convention: DayCountConvention | undefined): number {
    switch (convention) {
      case 'Actual/360':
      case '30/360':
        return 360;
      case 'Actual/365':
      default:
        return 365;
    }
  }

  /**
   * Calculate the number of days between two dates according to the specified convention
   * @param startDate The start date
   * @param endDate The end date
   * @param convention The day count convention to use
   * @returns The number of days between the dates
   */
  daysBetween(startDate: Date, endDate: Date, convention: DayCountConvention): number {
    const msPerDay = 1000 * 60 * 60 * 24;
    const diffMs = endDate.getTime() - startDate.getTime();
    
    switch (convention) {
      case 'Actual/365':
      case 'Actual/360':
        // Actual day count
        return Math.floor(diffMs / msPerDay);
      case '30/360':
        // 30/360 convention: assumes 30 days per month, 360 days per year
        const y1 = startDate.getFullYear();
        const m1 = startDate.getMonth() + 1;
        const d1 = Math.min(startDate.getDate(), 30);
        const y2 = endDate.getFullYear();
        const m2 = endDate.getMonth() + 1;
        const d2 = Math.min(endDate.getDate(), 30);
        
        return 360 * (y2 - y1) + 30 * (m2 - m1) + (d2 - d1);
      default:
        return Math.floor(diffMs / msPerDay);
    }
  }

  /**
   * Returns the accrual day units for a single calendar day step.
   * For actual conventions this is always 1. For 30/360 this can vary at month-end.
   */
  getAccrualDaysForDate(date: Date, convention: DayCountConvention | undefined): number {
    if (convention !== '30/360') {
      return 1;
    }

    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + 1);
    return Math.max(0, this.daysBetween(date, nextDate, '30/360'));
  }
}
